/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Payments_PaymentProcessorId as PaymentProcessorId } from '__generated__/graphql-types';

import Imgix from 'js/components/Imgix';
import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';

import { IconButton, TextField } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { setActiveWalletId } from 'bundles/account-settings/actions/EnterCardFormActions';
import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import {
  getPaymentMethodType,
  getWalletDescription,
} from 'bundles/account-settings/components/sections/payment-method/walletHelpers';
import { UpdateStates } from 'bundles/account-settings/constants/SettingsConstants';
import type { PaymentWallet } from 'bundles/account-settings/types/Payment';
import type SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';
import PaymentProcessorTypes, { type PaymentProcessorType } from 'bundles/payments-common/common/PaymentProcessorTypes';
import { STRIPE_CONSUMER_PAYMENT_PROCESSOR_IDS, paypalLogo } from 'bundles/payments-common/common/constants';
import { useSubscriptionPaymentMethodUpdateUrl } from 'bundles/payments-common/data/useSubscriptionPaymentMethodUpdateUrl';
import { getCardBrandIcon, getWalletTypeIcon } from 'bundles/payments-common/utils/walletUtils';

import _t from 'i18n!nls/account-settings';

const isStripeConsumerPaymentProcessorId = (
  id: PaymentProcessorType | PaymentProcessorId | null | undefined
): id is PaymentProcessorId => STRIPE_CONSUMER_PAYMENT_PROCESSOR_IDS.includes(id as PaymentProcessorId);

type CurrentWalletProps = {
  updateStatusDelete: {
    state: string;
  };
  wallet: PaymentWallet;
  activeWalletId: number | null | undefined;
  subscriptions: SubscriptionsV1[];
};

const styles = {
  wallet: css`
    display: flex;
    flex-direction: column;
  `,
  row: css`
    display: flex;
    align-items: flex-end;
    gap: var(--cds-spacing-100);
    margin-bottom: var(--cds-spacing-100);
  `,
  field: css`
    flex: 1;
    min-width: 0;
  `,
  prefixIcons: css`
    display: inline-flex;
    align-items: center;
    gap: var(--cds-spacing-50);
  `,
  walletTypeIcon: css`
    height: 24px;
  `,
  cardBrandIcon: css`
    height: 16px;
    aspect-ratio: 13 / 8;
  `,
  paypalLogo: css`
    height: 18px;
  `,
};

const CurrentWallet = ({ updateStatusDelete, wallet, activeWalletId, subscriptions }: CurrentWalletProps) => {
  const track = useTracker();
  const executeAction = useFluxibleAction();
  const redirectToSubscriptionPaymentMethodUpdateUrl = useSubscriptionPaymentMethodUpdateUrl();
  const showEditButton =
    wallet.paymentProcessorId === PaymentProcessorTypes.braintree
      ? subscriptions.some((subscription) => subscription.isWalletPayment)
      : subscriptions.some(
          // paymentWalletId is an ID string; PaymentWallet.id is a number
          (subscription) => subscription.isStripePayment && subscription.paymentWalletId === String(wallet.id)
        );

  const cardBrandIcon = getCardBrandIcon(wallet.creditCardType, wallet.paymentProcessorId);
  const walletTypeIcon = getWalletTypeIcon(wallet.walletType);

  const trackingData = {
    savedPaymentMethod: {
      id: wallet.id.toString(),
      paymentMethod: {
        type: getPaymentMethodType(wallet.walletType, wallet.paymentProcessorId),
      },
      paymentProcessor: { id: wallet.paymentProcessorId || PaymentProcessorTypes.braintree },
    },
  };
  const viewButtonVisibilityRef: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker(
    'view_button',
    {
      button: {
        name: 'account_setting_edit_payment_method_btn',
      },
      ...trackingData,
    }
  );
  const deleteButtonVisibilityRef: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker(
    'view_button',
    {
      button: {
        name: 'account_setting_delete_payment_method_btn',
      },
      ...trackingData,
    }
  );

  const handleDeleteCard = () => {
    track('click_button', {
      button: { name: 'account_setting_delete_payment_method_btn' },
      ...trackingData,
    });
    SettingsActionCreators.showDeleteCreditCardModal(wallet.id);
  };

  const handleStripeWalletEdit = async () => {
    const { paymentProcessorId } = wallet;
    if (!isStripeConsumerPaymentProcessorId(paymentProcessorId)) return;

    await redirectToSubscriptionPaymentMethodUpdateUrl(paymentProcessorId);
  };

  const handleEditCard = () => {
    track('click_button', {
      button: { name: 'account_setting_edit_payment_method_btn' },
      ...trackingData,
    });
    if (isStripeConsumerPaymentProcessorId(wallet.paymentProcessorId)) {
      handleStripeWalletEdit();
    } else {
      executeAction(setActiveWalletId, wallet.id);
    }
  };

  const isDeleted = updateStatusDelete.state === UpdateStates.SUCCEEDED;

  const walletDescription = getWalletDescription(
    wallet.paymentProcessorId,
    wallet.email,
    wallet.creditCardType,
    wallet.creditCardLastFourDigits
  );

  const renderCreditCardSection = () => {
    const { creditCardLastFourDigits } = wallet;
    const creditCardNumber = `XXXX XXXX XXXX ${creditCardLastFourDigits || 'XXXX'}`;

    return (
      <div css={styles.row}>
        <div css={styles.field}>
          <TextField
            label={_t('Card number')}
            value={creditCardNumber}
            readOnly
            fullWidth
            prefix={
              <span css={styles.prefixIcons}>
                {walletTypeIcon && <Imgix src={walletTypeIcon} alt="" css={styles.walletTypeIcon} />}
                <Imgix src={cardBrandIcon} alt="" css={styles.cardBrandIcon} />
              </span>
            }
          />
        </div>

        {showEditButton && (
          <IconButton
            intent="edit"
            variant="secondary"
            aria-label={_t('Edit #{walletDescription}', { walletDescription })}
            onClick={handleEditCard}
            disabled={isDeleted}
            ref={(buttonElement: HTMLButtonElement | null) => {
              viewButtonVisibilityRef.current = buttonElement;
            }}
          />
        )}

        <IconButton
          intent="delete"
          variant="ghost"
          aria-label={_t('Delete #{walletDescription}', { walletDescription })}
          onClick={handleDeleteCard}
          disabled={isDeleted}
          ref={(buttonElement: HTMLButtonElement | null) => {
            deleteButtonVisibilityRef.current = buttonElement;
          }}
        />
      </div>
    );
  };

  const renderPaypalSection = () => {
    const { email } = wallet;

    return (
      <div css={styles.row}>
        <div css={styles.field}>
          <TextField
            label={_t('PayPal Account')}
            value={email || ''}
            readOnly
            fullWidth
            prefix={<Imgix src={paypalLogo} alt="" css={styles.paypalLogo} />}
          />
        </div>
        <IconButton
          intent="delete"
          variant="ghost"
          aria-label={_t('Delete #{walletDescription}', { walletDescription })}
          onClick={handleDeleteCard}
          disabled={isDeleted}
          ref={(buttonElement: HTMLButtonElement | null) => {
            deleteButtonVisibilityRef.current = buttonElement;
          }}
        />
      </div>
    );
  };

  const renderWallet = (paymentProcessorId: string | null | undefined): React.ReactNode => {
    switch (paymentProcessorId) {
      case PaymentProcessorTypes.paypal:
        return renderPaypalSection();
      case PaymentProcessorTypes.braintree:
      case PaymentProcessorId.StripeConsumer:
      case PaymentProcessorId.StripeConsumerSandbox:
      case PaymentProcessorId.StripeBvConsumer:
      case PaymentProcessorId.StripeBvConsumerSandbox:
        return renderCreditCardSection();
      default:
        return null;
    }
  };

  return <div css={styles.wallet}>{renderWallet(wallet.paymentProcessorId)}</div>;
};

export default CurrentWallet;
